import * as React from 'react';
import { Box, Checkbox, Divider, LinearProgress, List, ListItem, ListItemButton, TextField, Typography } from '@mui/material';
import { ModalBase } from '../../components/ModalBase';
import { Palette, Sizes } from '../../@shared/palette';
import { AITrainModelQueueStateTypeEnum, AITrainModelStateTypeEnum, SchemaItem, StructureItemType, TrainedModel } from '../../@shared/Models/Models';
import { BadgeBoxSx, FlexBoxInline, InputBaseSx } from '../../@shared/templates';
import { useBadge } from '../../hooks/useBadge';
import { NotificationModel, NotificationModelType } from '../../reducers/main/mainModel';
import { useDispatch } from 'react-redux';
import { onThrowAlert } from '../../sagas/dataSaga/dataSagaActions';
import { PublishingProviderInstance } from '../../@shared/services/PublishingProvider';
import { isPublishingJobActive, PublishingJob, PublishingJobStatus } from '../../@shared/Models/PublishingModels';

export type PublishToCloudDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    schemaId: string;
    schemaName: string;
    schemaItems: SchemaItem[];
    models: TrainedModel[];
}


const JOB_POLL_INTERVAL_MS = 2000;
const JOB_POLL_MAX_FAILURES = 5;

const toMb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);

const isModelEligible = (model: TrainedModel) =>
    model.modelStateId === AITrainModelStateTypeEnum.Trained &&
    model.queueStateId === AITrainModelQueueStateTypeEnum.NotInQueue;

export const PublishToCloudDialog = ({isOpen, onClose, schemaId, schemaName, schemaItems, models}: PublishToCloudDialogProps) => {
    const [selectedModelIds, setSelectedModelIds] = React.useState<string[]>([]);
    const [name, setName] = React.useState<string>('');
    const [prefix, setPrefix] = React.useState<string>('');
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [job, setJob] = React.useState<PublishingJob | undefined>();
    const isJobActive = isPublishingJobActive(job);
    const pollFailures = React.useRef(0);
    const { getBadgeColor } = useBadge();
    const dispatch = useDispatch();

    const hasDependencies = React.useMemo(
        () => schemaItems.some((item) => item.valueTypeId === StructureItemType.Model),
        [schemaItems]
    );

    const handleToggle = React.useCallback((model: TrainedModel) => {
        if (!isModelEligible(model)) return;
        setSelectedModelIds((prev) =>
            prev.includes(model.id)
                ? prev.filter((id) => id !== model.id)
                : [...prev, model.id]
        );
    }, []);

    const handleSave = React.useCallback(async () => {
        setIsProcessing(true);
        try {
            const response = await PublishingProviderInstance.publishInitSelfHosted({
                id: schemaId,
                name: name || schemaName,
                prefix: hasDependencies ? prefix.trim() : undefined,
                modelIds: selectedModelIds,
            });
            if(response.status !== 200) {
                throw new Error((response.data as string) || "Failed to publish to cloud");
            }
            setJob(response.data as PublishingJob);
        } catch (e: any) {
            dispatch(onThrowAlert({
                message: e?.response?.data?.message || e?.message || "Failed to publish to cloud",
                lifeTime: 10000,
                type: NotificationModelType.Error
            } as NotificationModel));
        } finally {
            setIsProcessing(false);
        }
    }, [schemaId, name, schemaName, hasDependencies, prefix, selectedModelIds, dispatch]);

    React.useEffect(() => {
        if (!isOpen || !job || !isJobActive) return;

        const timer = setTimeout(async () => {
            try {
                const response = await PublishingProviderInstance.getSelfHostedJob(job.id);
                if (response.status !== 200) {
                    throw new Error((response.data as string) || "Failed to get publishing status");
                }
                pollFailures.current = 0;
                setJob(response.data as PublishingJob);
            } catch (e: any) {
                pollFailures.current++;
                if (pollFailures.current < JOB_POLL_MAX_FAILURES) {
                    setJob({ ...job });
                    return;
                }

                pollFailures.current = 0;
                setJob(undefined);
                dispatch(onThrowAlert({
                    message: `Unable to get publishing status: ${e?.message || "unknown error"}. Publishing may still finish in the background.`,
                    lifeTime: 10000,
                    type: NotificationModelType.Error
                } as NotificationModel));
            }
        }, JOB_POLL_INTERVAL_MS);

        return () => clearTimeout(timer);
    }, [isOpen, job, isJobActive, dispatch]);

    React.useEffect(() => {
        if (job?.status === PublishingJobStatus.Completed) {
            dispatch(onThrowAlert({
                message: `Published "${job.result?.schemaName ?? name}" to the cloud`,
                lifeTime: 5000,
                type: NotificationModelType.Success
            } as NotificationModel));
            onClose();
        } else if (job?.status === PublishingJobStatus.Failed) {
            dispatch(onThrowAlert({
                message: job.error || "Failed to publish to cloud",
                lifeTime: 10000,
                type: NotificationModelType.Error
            } as NotificationModel));
            setJob(undefined);
        }
    }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = React.useCallback(() => {
        if (isJobActive) {
            dispatch(onThrowAlert({
                message: "Publishing continues in the background",
                lifeTime: 5000,
                type: NotificationModelType.Warning
            } as NotificationModel));
        }
        onClose();
    }, [isJobActive, dispatch, onClose]);

    React.useEffect(() => {
        if (!isOpen) {
            setJob(undefined);
            setSelectedModelIds([]);
            setName('');
            setPrefix('');
        } else {
            setName(schemaName);
        }
    }, [isOpen, schemaName]);

    return (
        <ModalBase
            isOpen={isOpen}
            onCancel={handleClose}
            onSave={handleSave}
            title="Publish to Cloud"
            isDisabledSave={selectedModelIds.length === 0 || !name.trim() || (hasDependencies && !prefix.trim()) || isProcessing || isJobActive}
            isProcessing={isProcessing}
            primaryBtnText="Publish"
            secondBtnText={isJobActive ? "Close" : "Cancel"}
        >
            <Box sx={{ margin: "20px 0" }}>
                {isJobActive && job && <Box sx={{ marginBottom: "20px" }}>
                    <LinearProgress
                        variant={job.totalBytes > 0 ? "determinate" : "indeterminate"}
                        value={job.totalBytes > 0 ? job.uploadedBytes / job.totalBytes * 100 : 0}
                    />
                    <Typography sx={{ color: Palette.textColorSecondary, fontSize: Sizes.textSecondary, marginTop: "5px" }}>
                        {job.totalBytes > 0
                            ? `Uploading models: ${toMb(job.uploadedBytes)} of ${toMb(job.totalBytes)} MB`
                            : "Preparing models..."}
                    </Typography>
                </Box>}
                <TextField
                    sx={{ ...InputBaseSx, marginBottom: "20px" }}
                    size="small"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    label="Schema Name"
                    variant="outlined"
                />
                {hasDependencies && <TextField
                    sx={{ ...InputBaseSx, marginBottom: "20px" }}
                    size="small"
                    fullWidth
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value)}
                    label="Prefix for dependencies"
                    helperText="Added to the name of every dependent schema that will be created in the cloud"
                    variant="outlined"
                />}
                <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary, marginBottom: "10px" }}>
                    Select models to publish:
                </Typography>
                <List sx={{ width: '100%', padding: "0" }}>
                    {models.map((model, i) => {
                        const eligible = isModelEligible(model);
                        const typeColor = getBadgeColor(model.modelTypeId);
                        const stateColor = getBadgeColor(model.modelStateId);
                        const queueColor = getBadgeColor(model.queueStateId);

                        return <Box key={model.id}>
                            <ListItem sx={{ padding: 0, opacity: eligible ? 1 : 0.4 }}>
                                <ListItemButton onClick={() => handleToggle(model)} disabled={!eligible}>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedModelIds.includes(model.id)}
                                        tabIndex={-1}
                                        disableRipple
                                        disabled={!eligible}
                                        sx={{ marginRight: "10px" }}
                                    />
                                    <Box>
                                        <Box sx={{ ...FlexBoxInline, gap: "10px", alignItems: "flex-start", marginBottom: "5px" }}>
                                            <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>{model.name}</Typography>
                                        </Box>
                                        {model.description && <Typography sx={{ fontSize: Sizes.textSecondary, color: Palette.textColorSecondary, marginBottom: "5px" }}>{model.description}</Typography>}
                                        <Box sx={{ ...FlexBoxInline, fontSize: Sizes.pageSecondTitle }}>
                                            <Box sx={{ ...BadgeBoxSx, background: typeColor.backgroundColor }}>
                                                <Typography sx={{ fontSize: Sizes.textSecondary, color: typeColor.textColor }}>{model.modelTypeId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
                                            </Box>
                                            <Box sx={{ ...BadgeBoxSx, background: stateColor.backgroundColor }}>
                                                <Typography sx={{ fontSize: Sizes.textSecondary, color: stateColor.textColor }}>{model.modelStateId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
                                            </Box>
                                            <Box sx={{ ...BadgeBoxSx, background: queueColor.backgroundColor }}>
                                                <Typography sx={{ fontSize: Sizes.textSecondary, color: queueColor.textColor }}>{model.queueStateId.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                            {i !== models.length - 1 && <Divider variant="middle" component="li" />}
                        </Box>
                    })}
                </List>
            </Box>
        </ModalBase>
    );
};
